import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HeartHandshake, Sunrise, Moon, HandHelping, MessageCircle, Mic, Square, Send, Play, BookHeart } from "lucide-react";
import LiveButton from "./LiveButton";
import { portalApi, apiError, uploadVoice, mediaBlobUrl } from "@/lib/portalApi";
import { toast } from "sonner";

const MODES = [
  { kind: "text", label: "Message", icon: MessageCircle },
  { kind: "reflection", label: "Share a reflection", icon: BookHeart },
  { kind: "checkin_morning", label: "Morning check-in", icon: Sunrise },
  { kind: "checkin_evening", label: "Evening check-in", icon: Moon },
  { kind: "prayer", label: "Prayer", icon: HandHelping },
];
const KIND_LABEL = { text: "", reflection: "Weekly reflection", checkin_morning: "Morning check-in", checkin_evening: "Evening check-in", prayer: "Prayer", voice: "Voice note" };
const time = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function VoiceBubble({ path }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (url) return;
    setLoading(true);
    try { setUrl(await mediaBlobUrl(path)); } catch { toast.error("Couldn't load voice note."); } finally { setLoading(false); }
  };
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (url) return <audio data-testid="duo-voice-audio" src={url} controls className="w-full max-w-[240px] mt-1" />;
  return (
    <button data-testid="duo-voice-play" onClick={load} className="inline-flex items-center gap-2 mt-1 text-sm">
      <Play className="w-4 h-4" strokeWidth={1.5} />{loading ? "Loading…" : "Play voice note"}
    </button>
  );
}

export default function Duo() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wStatus, setWStatus] = useState(null);
  const navigate = useNavigate();
  const [mode, setMode] = useState("text");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const bottomRef = useRef(null);

  const load = () => portalApi.get("/portal/duo").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); portalApi.get("/portal/witness/status").then((r) => setWStatus(r.data)).catch(() => {}); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data]);

  const appendMessage = (msg) => setData((d) => ({ ...d, messages: [...d.messages, msg] }));

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data: msg } = await portalApi.post("/portal/duo/messages", { kind: mode, text });
      appendMessage(msg); setText("");
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        try {
          const up = await uploadVoice(blob);
          const { data: msg } = await portalApi.post("/portal/duo/messages", { kind: "voice", voice_url: up.path });
          appendMessage(msg);
        } catch (e) { toast.error(apiError(e)); }
      };
      recRef.current = rec; rec.start(); setRecording(true);
    } catch { toast.error("Microphone access is needed to record a voice note."); }
  };
  const stopRec = () => { recRef.current?.stop(); setRecording(false); };

  if (loading) return <p className="text-[#8a7a6a] font-body" data-testid="portal-duo">Loading…</p>;

  if (!data?.partner) {
    return (
      <div data-testid="portal-duo" className="max-w-2xl">
        <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">You & your witness · reciprocal</p>
        <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-6">Witness Room</h1>
        <div className="bg-white/60 border border-[#E0D8C9] rounded-xl p-8 text-center">
          <HeartHandshake className="w-8 h-8 text-[#A9724E] mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-serif-display text-2xl text-[#2A2825] mb-2">No witness yet.</p>
          <p className="text-[#5E4B3C] font-body">When Dej pairs you with a Witness, this becomes your permanent, private room for reflections, prayer, voice notes, and video — flowing both ways. Nothing here is ever deleted.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="portal-duo" className="max-w-2xl">
      <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">You & your witness · reciprocal</p>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-1">Witness Room</h1>
      <p className="text-[#5E4B3C] font-body mb-5">You & <Link to={`/portal/profile/${data.partner.id}`} data-testid="witness-profile-link" className="text-[#A9724E] hover:underline">{data.partner.name}</Link> — a permanent private room. You witness each other; either of you can pour out here. <span className="text-[#8a7a6a] italic">Your Witness rotates each week.</span></p>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <LiveButton scope="witness" title={`Live with ${data.partner.name.split(" ")[0]}`} label="Start a live call" testid="witness-go-live" />
        <button onClick={async () => { try { const { data: c } = await portalApi.post("/portal/conversations", { type: "dm", member_ids: [data.partner.id] }); navigate(`/portal/messages?c=${c.id}`); } catch (e) { toast.error(apiError(e)); } }}
          data-testid="witness-message" className="inline-flex items-center gap-2 rounded-full border border-[#D8CEBE] hover:bg-white/60 text-[#5E4B3C] px-4 py-2 text-sm transition-colors">
          <MessageCircle className="w-4 h-4" strokeWidth={1.5} />Message
        </button>
      </div>

      {wStatus?.needs_love && (
        <div data-testid="witness-needs-love" className="bg-[#EAE3D9] border border-[#A9724E]/30 rounded-xl p-4 mb-6 flex items-start gap-3">
          <HeartHandshake className="w-5 h-5 text-[#A9724E] mt-0.5 shrink-0" strokeWidth={1.5} />
          <p className="text-sm text-[#5E4B3C] font-body">{data.partner.name.split(" ")[0]} {wStatus.not_started_today ? "hasn't started today's journey yet" : "has been quiet for a few days"}. They might need some prayer — send a word of love.</p>
        </div>
      )}

      <div className="bg-white/50 border border-[#E0D8C9] rounded-xl p-5 mb-4 max-h-[52vh] overflow-y-auto space-y-3">
        {data.messages.length === 0 && <p className="text-center text-[#8a7a6a] font-body py-8">Start with a check-in. How is your soul today?</p>}
        {data.messages.map((m) => (
          <div key={m.id} data-testid="duo-message" className={`flex ${m.is_me ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.is_me ? "bg-[#A9724E] text-[#F4EFE6]" : "bg-white border border-[#E0D8C9] text-[#2A2825]"}`}>
              {KIND_LABEL[m.kind] && <p className={`text-[10px] tracking-overline uppercase mb-0.5 ${m.is_me ? "text-[#F4EFE6]/80" : "text-[#A9724E]"}`}>{!m.is_me && m.kind === "reflection" ? `From ${data.partner.name}` : KIND_LABEL[m.kind]}</p>}
              {m.kind === "voice" ? <VoiceBubble path={m.voice_url} /> : <p className="font-body leading-relaxed whitespace-pre-wrap">{m.text}</p>}
              <p className={`text-[10px] mt-1 ${m.is_me ? "text-[#F4EFE6]/70" : "text-[#8a7a6a]"}`}>{time(m.created_at)}</p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {MODES.map((mo) => (
          <button key={mo.kind} data-testid={`duo-mode-${mo.kind}`} onClick={() => setMode(mo.kind)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${mode === mo.kind ? "bg-[#2A2825] text-[#F4EFE6]" : "bg-[#EAE3D9] text-[#5E4B3C] hover:bg-[#e0d8c9]"}`}>
            <mo.icon className="w-3.5 h-3.5" strokeWidth={1.5} />{mo.label}
          </button>
        ))}
      </div>

      <div className="flex items-end gap-2">
        <textarea data-testid="duo-input" value={text} onChange={(e) => setText(e.target.value)} rows={2}
          placeholder={mode === "prayer" ? "How can your partner pray for you?" : "Write to your partner…"}
          className="flex-1 bg-white border border-[#D8CEBE] rounded-xl px-4 py-3 text-[#2A2825] font-body outline-none focus:border-[#A9724E] resize-none" />
        {recording ? (
          <button data-testid="duo-record-stop" onClick={stopRec} className="rounded-full bg-[#b45c5c] text-white p-3.5 transition-colors animate-pulse">
            <Square className="w-4 h-4" strokeWidth={2} fill="currentColor" />
          </button>
        ) : (
          <button data-testid="duo-record-start" onClick={startRec} title="Record a voice note" className="rounded-full bg-[#EAE3D9] hover:bg-[#e0d8c9] text-[#A9724E] p-3.5 transition-colors">
            <Mic className="w-4 h-4" strokeWidth={1.5} />
          </button>
        )}
        <button data-testid="duo-send" onClick={send} disabled={sending || !text.trim()} className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] p-3.5 transition-colors disabled:opacity-50">
          <Send className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
      {recording && <p className="text-xs text-[#b45c5c] font-body mt-2">Recording… tap the stop button when you're done.</p>}
    </div>
  );
}
